import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import {Helmet} from 'react-helmet';
import {title} from 'lib/head';

import {Button, Dialogue, Modal, Heading, Text, Card, Icon, ButtonGroup} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {callAction} from 'client/framework';
import {genericMandarkRequest} from 'resources/mandark.resource';
import sessionStore from 'client/Session/SessionStore';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';

import './deactivate-account.scss';

import SadPanda from './sad_panda.svg';

const ConsiderationListItem = ({children}) => (
  <Text as='li' size='l' className='u-text-align_center u-pad_2'>
    {children}
  </Text>
);

ConsiderationListItem.propTypes = {
  children: PropTypes.node
};

async function deactivate() {
  const query = resource('users_v2').mandarkEndpoint(['users_v2', sessionStore.getUserId()]).done();

  try {
    await genericMandarkRequest('patch', query, {
      data: {
        attributes: {
          status: 'inactive'
        }
      }
    });
    callAction(sessionLifecycleActions.END_SESSION);
  } catch (e) {
    logger.error(
      'failed to deactivate user %s: %s',
      sessionStore.getUserId(),
      e.message || 'unknown error'
    );
  }
}

const DeactivateAccount = () => {
  const [isModalVisible, setModalVisibility] = useState(false);
  const handleClose = () => setModalVisibility(false);
  return (
    <>
      <Helmet>{title('My Account | Deactivate | Albert')}</Helmet>
      <Card paddingLevel={5} className='deactivate-account u-mar-b_6 u-mar-lr_auto'>
        <div className='u-display_flex u-align-items_center u-flex-direction_column'>
          <div className='u-text-align_center'>
            <img className='deactivate-account__sad-panda' src={SadPanda} alt='' />
            <Heading as='h1' size='m' className='deactivate-account__heading'>
              Some things to consider...
            </Heading>
          </div>
          <ul className='deactivate-account__considerations u-max-width_text'>
            <ConsiderationListItem>
              You will no longer be able to access your account.
            </ConsiderationListItem>
            <ConsiderationListItem>
              You will not be able to sign up for another account with the same email or username.
            </ConsiderationListItem>
            <ConsiderationListItem>
              All data associated with your account will be kept for reporting and compliance
              reasons.
            </ConsiderationListItem>
            <ConsiderationListItem>
              Your data up until deactivation will continue to be shared with any teacher and school
              that had previously had access to such data.
            </ConsiderationListItem>
          </ul>
          <div className='u-width_100pc'>
            <ButtonGroup direction='column' fillColumn>
              <Button
                color='secondary'
                onClick={() => {
                  setModalVisibility(true);
                }}
              >
                Deactivate account
              </Button>
              <Button as={Link} to='/account/settings'>
                Go back
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Card>
      {isModalVisible && (
        <Modal ariaLabel='Confirm the deactivation of your account.' handleClose={handleClose}>
          {() => {
            return (
              <Dialogue
                inModal
                smallWidth
                handleClose={handleClose}
                alignTitle='center'
                hideCloseBtn
                title={<Icon icon='exclamation-triangle' iconStyle='regular' />}
              >
                <Dialogue.Body align='center'>
                  Are you sure you want to deactivate your account?
                </Dialogue.Body>
                <Dialogue.BtnGroup className='deactivate-account-dialogue_buttons'>
                  <Button onClick={handleClose} color='secondary'>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      deactivate();
                      handleClose();
                    }}
                    color='destructive'
                  >
                    Deactivate account
                  </Button>
                </Dialogue.BtnGroup>
              </Dialogue>
            );
          }}
        </Modal>
      )}
    </>
  );
};

export default DeactivateAccount;
